import { DataGridColumn } from '@virtus/components/DataGrid/models';
import { Reducer } from 'redux';
import { ErrorDetailsColumns } from 'src/generators/errorDetailsColumns';
import { EventDetailsColumns } from 'src/generators/eventDetailsColumns';
import { SignalRDetailsColumns } from 'src/generators/signalRDetailsColumns';
import { UserDetailsColumns } from 'src/generators/userDetailsColumns';
import { ApplicationDetailsColumns } from 'src/generators/applicationDetailsColumns';
import { ExceptionDetailsColumns } from 'src/generators/exceptionDetailsColumns';
import { TraceDetailsColumns } from 'src/generators/traceDetailsColumns';
import { ActionType, createStandardAction, getType } from 'typesafe-actions';
import { Actions, RootState } from './rootReducer';

export type Group = {
  checked: boolean;
  group: string | null;
} & DataGridColumn;

type GroupsChild = Group | Group[];

export type Groups = GroupsChild[];

const errorLogColumns = ErrorDetailsColumns.reduce((acc: any, curr: DataGridColumn) => {
  acc.push({ ...curr, group: null, checked: true });
  return acc;
}, []);

const signalRLogColumns = SignalRDetailsColumns.reduce((acc: any, curr: DataGridColumn) => {
  acc.push({ ...curr, group: null, checked: true });
  return acc;
}, []);

const eventLogColumns = EventDetailsColumns.reduce((acc: any, curr: DataGridColumn) => {
  acc.push({ ...curr, group: null, checked: true });
  return acc;
}, []);

const userLogColumns = UserDetailsColumns.reduce((acc: any, curr: DataGridColumn) => {
  acc.push({ ...curr, group: null, checked: true });
  return acc;
}, []);

const applicationColumns = ApplicationDetailsColumns.reduce((acc: any, curr: DataGridColumn) => {
  acc.push({ ...curr, group: null, checked: true });
  return acc;
}, []);

const exceptionColumns = ExceptionDetailsColumns.reduce((acc: any, curr: DataGridColumn) => {
  acc.push({ ...curr, group: null, checked: true });
  return acc;
}, []);
const traceColumns = TraceDetailsColumns.reduce((acc: any, curr: DataGridColumn) => {
  acc.push({ ...curr, group: null, checked: true });
  return acc;
}, []);

export type InspectorProps = {
  type: string;
};

export const inspectorActions = {
  open: createStandardAction('@inspector/open')<InspectorProps>(),
  openErrorLogInspector: createStandardAction('@inspector/openErrorLogInspector')<{ errorLog: ErrorLog }>(),
  openEventLogInspector: createStandardAction('@inspector/openEventLogInspector')<{ eventLog: EventLog }>(),
  openApplicationLogInspector: createStandardAction('@inspector/openApplicationLogInspector')<{
    applicationLog: ApplicationLog;
  }>(),
  
  close: createStandardAction('@inspector/close')<any>(),

  setErrorLog: createStandardAction('@inspector/setErrorLog')<ErrorLog>(),
  setSignalRLog: createStandardAction('@inspector/setSignalRLog')<SignalRLog>(),
  setEventLog: createStandardAction('@inspector/setEventLog')<EventLog>(),
  setUserLog: createStandardAction('@inspector/setUserLog')<UserLog>(),
  setApplicationLog: createStandardAction('@inspector/setApplicationLog')<ApplicationLog>(),
  setExceptionLog: createStandardAction('@inspector/setExceptionLog')<ExceptionLog>(),
  setTraceLog: createStandardAction('@inspector/setTraceLog')<TraceLog>(),

  setAppInsightKeys:  createStandardAction('@inspector/setAppInsightKeys')<any>(),
};

export type InspectorActions = ActionType<typeof inspectorActions>;

export type InspectorState = {
  readonly open: boolean;
  readonly errorLog: ErrorLog | null;
  readonly eventLog: EventLog | null;
  readonly applicationLog: ApplicationLog | null;
  readonly exceptionLog: ExceptionLog | null;
  readonly traceLog: TraceLog | null;
  readonly type: string;

  readonly errorLogColumns: Groups;
  readonly signalRLogColumns: Groups;
  readonly eventLogColumns: Groups;
  readonly userLogColumns: Groups;
  readonly applicationColumns: Groups;
  readonly exceptionColumns: Groups;
  readonly traceColumns: Groups;

  readonly appInsightAppId: string;
  readonly appInsightApiKey: string;
};

export const inspectorInitialState: InspectorState = {
  open: false,
  errorLog: null,
  eventLog: null,
  applicationLog: null,
  exceptionLog: null,
  traceLog: null,
  type: '',
  errorLogColumns,
  signalRLogColumns,
  eventLogColumns,
  userLogColumns,
  applicationColumns,
  exceptionColumns,
  traceColumns,

  appInsightAppId: '',
  appInsightApiKey: '',
};

export const inspector: Reducer<InspectorState, Actions> = (state = inspectorInitialState, action) => {
  switch (action.type) {
    case getType(inspectorActions.open): {
      return { ...state, ...action.payload, errorLog: state.errorLog, open: true };
    }

    case getType(inspectorActions.openErrorLogInspector): {
      return { ...state, ...action.payload, open: true, type: 'errorlog' };
    }
    case getType(inspectorActions.openEventLogInspector): {
      return { ...state, ...action.payload, open: true, type: 'eventlog' };
    }
    case getType(inspectorActions.openApplicationLogInspector): {
      return { ...state, ...action.payload, open: true, type: 'applicationlog' };
    }
    
    case getType(inspectorActions.close): {
      return { ...state, open: false };
    }

    case getType(inspectorActions.setErrorLog): {
      return { ...state, errorLog: action.payload };
    }
    case getType(inspectorActions.setSignalRLog): {
      return { ...state, signalRLog: action.payload };
    }
    case getType(inspectorActions.setEventLog): {
      return { ...state, eventLog: action.payload };
    }
    case getType(inspectorActions.setUserLog): {
      return { ...state, userLog: action.payload };
    }
    case getType(inspectorActions.setApplicationLog): {
      return { ...state, applicationLog: action.payload };
    }
    case getType(inspectorActions.setExceptionLog): {
      return { ...state, exceptionLog: action.payload };
    }
    case getType(inspectorActions.setTraceLog): {
      return { ...state, traceLog: action.payload };
    }
    case getType(inspectorActions.setAppInsightKeys): {
      return { ...state, 
        appInsightAppId: action.payload.appInsightAppId,
        appInsightApiKey: action.payload.appInsightApiKey,
       };
    }
    default:
      return state;
  }
};

export const inspectorSelector = {
  open: (state: RootState) => state.inspector.open,
  errorLog: (state: RootState) => state.inspector.errorLog,
  eventLog: (state: RootState) => state.inspector.eventLog,
  applicationLog: (state: RootState) => state.inspector.applicationLog,
  exceptionLog: (state: RootState) => state.inspector.exceptionLog,
  traceLog: (state: RootState) => state.inspector.traceLog,
  type: (state: RootState) => state.inspector.type,

  errorLogColumns: (state: RootState) => state.inspector.errorLogColumns,
  signalRLogColumns: (state: RootState) => state.inspector.signalRLogColumns,
  eventLogColumns: (state: RootState) => state.inspector.eventLogColumns,
  userLogColumns: (state: RootState) => state.inspector.userLogColumns,
  applicationColumns: (state: RootState) => state.inspector.applicationColumns,
  exceptionColumns: (state: RootState) => state.inspector.exceptionColumns,
  traceColumns: (state: RootState) => state.inspector.traceColumns,

  appInsightAppId: (state: RootState) => state.inspector.appInsightAppId,
  appInsightApiKey: (state: RootState) => state.inspector.appInsightApiKey,
};

export interface ErrorLog {
  ErrorId: string;
  ErrorMessage?: string;
  ErrorSeverity?: string;
  MethodName?: string;
  ModuleName?: string;
  MachineName?: string;
  StackTrace?: string;
  LoggedUserId?: string;
  ExceptionOriginationDate?: string;
  SignalRClientId?: string;
  HttpHeader: string;
}

export interface SignalRLog {
  RemoteIpAddress?: string;
  TimeStamp?: string;
  ConnectionType: string;
  SignalRClientId?: string;
  Status?: string;
  EmailAddress?: string;
}

export interface EventLog {
  EventId: string;
  EventDescription?: string;
  EventSeverity?: string;
  MachineName?: string;
  IpAddress?: string;
  EventSource?: string;
  EventStatus?: string;
  EventTimeStamp?: string;
  LoggedinUser?: string;
  Type?: string;
  Parameters?: any;
}

export interface UserLog {
  UserName?: string;
  PartitionKey: string;
  TimeStamp?: string;
  LoginLocation?: string;
  GatewayIp?: string;
  LocalIp?: string;
  IsValidUser?: string;
  UserSamAccount?: string;
}

export interface ApplicationLog {
  RowKey?: string;
  PartitionKey?: string;
  TimeStamp?: string;
  Description?: string;
  ActiveDirAppId?: string;
  ActiveDirTenant?: string;
  AppInsightInstrumentKey?: string;
  AppInsightAppId?: string;
  AppInsightApiKey?: string;
  AppInsightUrlProtocol?: string;
  AppInsightUrl?: string;
  AppInfoUrls?: string;
}

export interface ExceptionLog {
  timestamp?: string;
  outerMessage?: string;
  innermostMessage?: string;
  outerMethod?: string;
  outerType?: string;
  roleInstance?:string;
}

export interface TraceLog{
  timestamp?:string;
  message?:string;
  name?:string;
  roleInstance?:string;

}
